/* Copyright (C) mroads, LLC - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Written by Premsagar Choudhary <premsagar.choudhary@mroads.com>, April 2021
 * @format
 * @flow
 */

import React, { forwardRef, useEffect, useRef, useImperativeHandle } from 'react';
import PropTypes from 'prop-types';

const UserInactivity = forwardRef((props, ref) => {
  const { children, onTimeOut, active, inactivityTime } = props;

  const intervalRef = useRef(null);
  const throttlerRef = useRef(null);

  useImperativeHandle(ref, () => ({
    resetInactivity: () => {
      resetUserActivityTimeout();
    },
  }));

  useEffect(() => {
    if (active) {
      startInactivityDetection();
      resetUserActivityTimeout();
    }
    return () => {
      clearTimeout(intervalRef.current);
      window.removeEventListener('mousemove', userActivityThrottler);
      window.removeEventListener('scroll', userActivityThrottler);
      window.removeEventListener('keydown', userActivityThrottler);
      window.removeEventListener('resize', userActivityThrottler);
      window.removeEventListener('click', userActivityThrottler);
    };
  }, []);

  const startInactivityDetection = () => {
    window.addEventListener('mousemove', userActivityThrottler);
    window.addEventListener('scroll', userActivityThrottler);
    window.addEventListener('keydown', userActivityThrottler);
    window.addEventListener('resize', userActivityThrottler);
    window.addEventListener('click', userActivityThrottler);
  };

  const userActivityThrottler = () => {
    if (!throttlerRef.current) {
      throttlerRef.current = setTimeout(() => {
        resetUserActivityTimeout();
        clearTimeout(throttlerRef.current);
        throttlerRef.current = null;
      }, 1000);
    }
  };

  const resetUserActivityTimeout = () => {
    clearTimeout(intervalRef.current);
    intervalRef.current = setTimeout(() => {
      onTimeOut && onTimeOut();
    }, inactivityTime);
  };

  return children;
});

export default UserInactivity;

UserInactivity.propTypes = {
  children: PropTypes.node,
  onTimeOut: PropTypes.func,
  active: PropTypes.bool,
  inactivityTime: PropTypes.number,
};

UserInactivity.defaultProps = {
  active: false,
  onTimeOut: () => { },
  inactivityTime: 180000,
};
